import { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';

import { loadStateToReplayBucket, ReplayBucket } from './replayBuckets';

// A gap between consecutive pings of at least this many minutes is treated as a
// loss of signal: the current segment ends and the gap is filled with an
// explicit UNKNOWN segment so it reads distinctly from real Idle. Mirrors the
// 15-minute no-signal threshold the LiveMap segmenters use.
const NO_SIGNAL_GAP_MINUTES = 15;
const MS_PER_MINUTE = 60_000;

export interface ReplaySegment {
  bucket: ReplayBucket;
  durationMin: number;
  endMs: number;
  loadId: string | null;
  // 1-based ordinal of the load within the job (by first-seen order); null for
  // non-load segments (no-signal gaps, idle/unknown between loads).
  loadSequence: number | null;
  startMs: number;
}

export interface ReplayRow {
  key: string;
  label: { truckNumber: string; vendor: string };
  segments: ReplaySegment[];
}

interface OpenSegment {
  bucket: ReplayBucket;
  endMs: number;
  loadId: string | null;
  startMs: number;
}

/**
 * Purpose-built segmenter for the Gantt (the LiveMap `useGet*Segments` hooks
 * return shapes that don't fit discrete per-load bars). For one truck's pings it:
 * 1. sorts by `occurredAt` (input is not assumed sorted);
 * 2. assigns `loadSequence` by first-seen order of distinct `loadId`;
 * 3. starts a new segment on a `loadId` change, a bucket change, or a no-signal
 *    gap — and fills the gap itself with an UNKNOWN segment;
 * 4. preserves single-ping loads/states (they become zero-duration segments);
 * 5. derives each bucket from `loadStateToReplayBucket`;
 * 6. reports `durationMin` from the segment span.
 */
export function computeReplaySegments(pings: LocationHistoryMinimal[]): ReplaySegment[] {
  if (pings.length === 0) return [];

  const sorted = [...pings].sort(
    (a, b) => a.occurredAt.valueOf() - b.occurredAt.valueOf(),
  );

  const loadSequenceById = new Map<string, number>();
  for (const ping of sorted) {
    if (ping.loadId && !loadSequenceById.has(ping.loadId)) {
      loadSequenceById.set(ping.loadId, loadSequenceById.size + 1);
    }
  }

  const segments: ReplaySegment[] = [];
  let open: OpenSegment | null = null;

  const finalize = (segment: OpenSegment): ReplaySegment => ({
    bucket: segment.bucket,
    durationMin: Math.round((segment.endMs - segment.startMs) / MS_PER_MINUTE),
    endMs: segment.endMs,
    loadId: segment.loadId,
    loadSequence: segment.loadId ? (loadSequenceById.get(segment.loadId) ?? null) : null,
    startMs: segment.startMs,
  });

  for (const ping of sorted) {
    const ms = ping.occurredAt.valueOf();
    const bucket = loadStateToReplayBucket(ping.loadState, ping.userIdleRaw);
    const loadId = ping.loadId;

    if (!open) {
      open = { bucket, endMs: ms, loadId, startMs: ms };
      continue;
    }

    const gapMinutes = (ms - open.endMs) / MS_PER_MINUTE;

    if (gapMinutes >= NO_SIGNAL_GAP_MINUTES) {
      segments.push(finalize(open));
      // Fill the no-signal span so the gap renders as Unknown, not Idle.
      segments.push({
        bucket: 'UNKNOWN',
        durationMin: Math.round((ms - open.endMs) / MS_PER_MINUTE),
        endMs: ms,
        loadId: null,
        loadSequence: null,
        startMs: open.endMs,
      });
      open = { bucket, endMs: ms, loadId, startMs: ms };
      continue;
    }

    if (open.loadId !== loadId || open.bucket !== bucket) {
      // Carry the boundary to the change ping so adjacent segments are
      // contiguous — otherwise the interval between the last ping of the old
      // status and the first ping of the new one is dropped, undercounting
      // durations on sparse GPS data.
      open.endMs = ms;
      segments.push(finalize(open));
      open = { bucket, endMs: ms, loadId, startMs: ms };
      continue;
    }

    open.endMs = ms;
  }

  if (open) segments.push(finalize(open));

  return segments;
}
